import React, { useState, useEffect, useCallback } from 'react';
import { Icon, Segment, Header, Button, Label, Table, Dropdown } from 'semantic-ui-react';

const TRIGGER_OPTIONS = [
  { key: 'all', value: '', text: 'All Types' },
  { key: 'webhook', value: 'webhook', text: 'Webhook' },
  { key: 'auto_sync', value: 'auto_sync', text: 'Auto Sync' },
  { key: 'manual_sync', value: 'manual_sync', text: 'Manual Sync' },
];

const STATUS_OPTIONS = [
  { key: 'all', value: '', text: 'All Status' },
  { key: 'processed', value: 'processed', text: 'Processed' },
  { key: 'failed', value: 'failed', text: 'Failed' },
  { key: 'processing', value: 'processing', text: 'Processing' },
  { key: 'received', value: 'received', text: 'Received' },
];

const statusColor = status => {
  switch (status) {
    case 'processed':
      return 'green';
    case 'failed':
      return 'red';
    case 'processing':
      return 'blue';
    default:
      return 'grey';
  }
};

const triggerIcon = type => {
  switch (type) {
    case 'webhook':
      return 'bolt';
    case 'auto_sync':
      return 'clock';
    case 'manual_sync':
      return 'hand pointer';
    default:
      return 'circle';
  }
};

const timeAgo = dateStr => {
  if (!dateStr) return 'N/A';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const RcmActivityLog = React.memo(({ accessToken, serverBaseUrl }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [triggerFilter, setTriggerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (triggerFilter) params.set('triggerType', triggerFilter);
      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(
        `${serverBaseUrl}/api/integrations/rcm/webhook-deliveries?${params}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        const result = data.item || data;
        setDeliveries(result.items || []);
        setTotal(result.total || 0);
      }
    } catch (err) {
      console.error('Error fetching deliveries:', err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, serverBaseUrl, page, triggerFilter, statusFilter]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  return (
    <Segment>
      <Header as="h4" style={{ marginBottom: '15px' }}>
        <Icon name="list alternate" />
        <Header.Content>
          Activity Log
          <Header.Subheader>{total} total events</Header.Subheader>
        </Header.Content>
      </Header>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', alignItems: 'center' }}>
        <Dropdown
          selection
          compact
          options={TRIGGER_OPTIONS}
          value={triggerFilter}
          onChange={(e, { value }) => {
            setTriggerFilter(value);
            setPage(1);
          }}
          style={{ minWidth: '140px' }}
        />
        <Dropdown
          selection
          compact
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(e, { value }) => {
            setStatusFilter(value);
            setPage(1);
          }}
          style={{ minWidth: '140px' }}
        />
        <Button icon="refresh" size="small" onClick={fetchDeliveries} loading={loading} />
      </div>

      {deliveries.length === 0 ? (
        <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
          No activity recorded yet. Events will appear here when bookings are synced or webhooks are
          received.
        </p>
      ) : (
        <>
          <Table compact size="small" basic="very">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Booking</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Result</Table.HeaderCell>
                <Table.HeaderCell>Time</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {deliveries.map(d => (
                <Table.Row
                  key={d.id}
                  negative={d.status === 'failed'}
                  positive={d.status === 'processed'}
                >
                  <Table.Cell>
                    <Icon name={triggerIcon(d.triggerType)} />
                    {d.triggerType === 'webhook'
                      ? 'Webhook'
                      : d.triggerType === 'auto_sync'
                      ? 'Auto'
                      : 'Manual'}
                  </Table.Cell>
                  <Table.Cell style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                    {d.reservationNo}
                  </Table.Cell>
                  <Table.Cell>
                    <Label size="tiny" color={statusColor(d.status)}>
                      {d.status}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    {d.cardsCreated > 0 && (
                      <Label size="tiny" basic color="green">
                        {d.cardsCreated} created
                      </Label>
                    )}
                    {d.cardsUpdated > 0 && (
                      <Label size="tiny" basic color="blue">
                        {d.cardsUpdated} updated
                      </Label>
                    )}
                    {d.cardsSkipped > 0 && (
                      <Label size="tiny" basic color="grey">
                        {d.cardsSkipped} skipped
                      </Label>
                    )}
                    {d.errorMessage && (
                      <span style={{ color: 'red', fontSize: '0.85em' }} title={d.errorMessage}>
                        <Icon name="warning sign" /> {d.errorMessage.substring(0, 40)}...
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'nowrap', fontSize: '0.85em', color: '#666' }}>
                    {timeAgo(d.receivedAt)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          {total > 15 && (
            <div
              style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}
            >
              <Button
                size="tiny"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                icon="chevron left"
              />
              <span style={{ lineHeight: '2.2em', fontSize: '0.9em' }}>
                Page {page} of {Math.ceil(total / 15)}
              </span>
              <Button
                size="tiny"
                disabled={page >= Math.ceil(total / 15)}
                onClick={() => setPage(p => p + 1)}
                icon="chevron right"
              />
            </div>
          )}
        </>
      )}
    </Segment>
  );
});

RcmActivityLog.displayName = 'RcmActivityLog';

export default RcmActivityLog;
